import { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertTriangle, X, CheckCircle2 } from 'lucide-react'

interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  /** Texto simples (quebras de linha preservadas). Ignorado se `content` for informado. */
  message?: string
  /** Corpo rico (resumos, destaques). Se definido, substitui `message`. */
  content?: ReactNode
  confirmText?: string
  cancelText?: string
  variant?: 'default' | 'danger' | 'warning'
  icon?: ReactNode
  /** Ex.: `max-w-2xl` para diálogos largos */
  maxWidthClassName?: string
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message = '',
  content,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  variant = 'default',
  icon,
  maxWidthClassName = 'max-w-md'
}: ConfirmDialogProps) {
  if (!isOpen) return null

  const variantStyles = {
    default: {
      iconBg: 'bg-brand-100',
      iconColor: 'text-brand-600',
      confirmBtn: 'bg-brand-500 hover:bg-brand-600'
    },
    danger: {
      iconBg: 'bg-red-100',
      iconColor: 'text-red-600',
      confirmBtn: 'bg-red-500 hover:bg-red-600'
    },
    warning: {
      iconBg: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      confirmBtn: 'bg-yellow-500 hover:bg-yellow-600'
    }
  }

  const styles = variantStyles[variant]
  const defaultIcon = variant === 'danger' ? (
    <AlertTriangle className="w-6 h-6" />
  ) : variant === 'warning' ? (
    <AlertTriangle className="w-6 h-6" />
  ) : (
    <CheckCircle2 className="w-6 h-6" />
  )

  const handleConfirm = () => {
    onConfirm()
  }

  return createPortal(
    <div
      className="fixed bg-black bg-opacity-50 flex items-center justify-center"
      style={{
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 9999998
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div className={`bg-white rounded-xl shadow-xl w-full mx-4 flex flex-col ${maxWidthClassName}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center space-x-3">
            <div className={`w-10 h-10 ${styles.iconBg} ${styles.iconColor} rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]`}>
              {icon || defaultIcon}
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 flex-shrink-0 min-h-0">
          {content != null ? (
            <div className="text-sm text-gray-700">{content}</div>
          ) : (
            <div className="text-sm text-gray-600 whitespace-pre-line">
              {message.replace(/\\n/g, '\n')}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 bg-gray-50 flex justify-end gap-3 px-6 py-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            className={`px-4 py-2 text-sm text-white ${styles.confirmBtn} rounded-lg transition-all duration-200 font-medium shadow-sm`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

